/**
 * Stripe Payment Service
 * Handles all Stripe payment processing for BookAPT
 * Supports: Credit Cards, Apple Pay, Google Pay
 */

import { Alert, Platform } from 'react-native';
import { STRIPE_CONFIG as APP_STRIPE_CONFIG } from '../config/stripeConfig';

// Stripe configuration
export const STRIPE_CONFIG = {
  // Use shared app config so web/mobile resolve from same environment source.
  publishableKey: APP_STRIPE_CONFIG.publishableKey,
  
  // IMPORTANT: Secret key should NEVER be in client code!
  // This is a placeholder - in production, ALL server-side operations
  // must be done through a backend API
  merchantIdentifier: 'merchant.com.bookapt',  // For Apple Pay
  merchantName: 'BookAPT',
  
  // API endpoint (RN: never localhost; use Render HTTPS for iOS ATS)
  apiEndpoint: APP_STRIPE_CONFIG.apiEndpoint,
};

export interface PaymentRequest {
  amount: number;  // in dollars (will be converted to cents)
  currency: string;
  serviceName: string;
  customerId: string;
  customerName?: string;
  customerEmail?: string;
  businessId: string;
  businessName?: string;
  metadata?: Record<string, any>;
}

export interface PaymentResult {
  success: boolean;
  transactionId?: string;
  paymentMethod?: string;
  error?: string;
  errorType?: 'card_error' | 'validation_error' | 'network_error' | 'processing_error';
}

export interface CardDetails {
  number: string;
  expMonth: number;
  expYear: number;
  cvc: string;
  name?: string;
  postalCode?: string;
}

class StripePaymentService {
  private isInitialized = false;

  /**
   * Initialize Stripe (should be called once at app startup)
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) return;

    try {
      console.log('Initializing Stripe Payment Service...');
      this.isInitialized = true;
      console.log('Stripe Payment Service initialized successfully');
    } catch (error) {
      console.error('Failed to initialize Stripe:', error);
      throw error;
    }
  }

  /**
   * Process Credit Card Payment
   * This is the main method for card payments
   */
  async processCreditCard(
    request: PaymentRequest,
    cardDetails: CardDetails
  ): Promise<PaymentResult> {
    try {
      console.log('Processing credit card payment:', {
        amount: request.amount,
        service: request.serviceName,
      });

      // Validate card details
      const validation = this.validateCardDetails(cardDetails);
      if (!validation.valid) {
        return {
          success: false,
          error: validation.error,
          errorType: 'validation_error',
        };
      }

      // Create PaymentIntent on backend, then confirm client-side with Stripe SDK.
      const paymentIntent = await this.createPaymentIntent(request);
      
      if (!paymentIntent) {
        return {
          success: false,
          error: 'Failed to create payment intent. Please try again.',
          errorType: 'processing_error',
        };
      }

      // Confirm payment with Stripe SDK (mobile) using PaymentSheet.
      const result = await this.confirmCardPayment(paymentIntent.clientSecret, cardDetails);
      
      return result;
    } catch (error: any) {
      console.error('Credit card payment error:', error);
      return {
        success: false,
        error: error.message || 'Payment processing failed. Please try again.',
        errorType: 'processing_error',
      };
    }
  }

  /**
   * Process Apple Pay Payment
   */
  async processApplePay(request: PaymentRequest): Promise<PaymentResult> {
    try {
      console.log('Processing Apple Pay payment:', {
        amount: request.amount,
        service: request.serviceName,
      });

      // Check if Apple Pay is available
      if (Platform.OS !== 'ios') {
        return {
          success: false,
          error: 'Apple Pay is only available on iOS devices',
          errorType: 'validation_error',
        };
      }

      // In test mode, simulate successful payment
      if (__DEV__) {
        await this.simulatePaymentDelay();
        return {
          success: true,
          transactionId: `ap_test_${Date.now()}`,
          paymentMethod: 'apple_pay',
        };
      }

      // Real Apple Pay implementation would use react-native-payments
      // and Stripe's Payment Request API
      
      return {
        success: false,
        error: 'Apple Pay integration pending. Please use credit card.',
        errorType: 'processing_error',
      };
    } catch (error: any) {
      console.error('Apple Pay error:', error);
      return {
        success: false,
        error: error.message || 'Apple Pay processing failed',
        errorType: 'processing_error',
      };
    }
  }

  /**
   * Process Google Pay Payment
   */
  async processGooglePay(request: PaymentRequest): Promise<PaymentResult> {
    try {
      console.log('Processing Google Pay payment:', {
        amount: request.amount,
        service: request.serviceName,
      });

      // Check if Google Pay is available
      if (Platform.OS !== 'android') {
        return {
          success: false,
          error: 'Google Pay is only available on Android devices',
          errorType: 'validation_error',
        };
      }

      // In test mode, simulate successful payment
      if (__DEV__) {
        await this.simulatePaymentDelay();
        return {
          success: true,
          transactionId: `gp_test_${Date.now()}`,
          paymentMethod: 'google_pay',
        };
      }

      // Real Google Pay implementation would use react-native-google-pay
      // and Stripe's Payment Request API
      
      return {
        success: false,
        error: 'Google Pay integration pending. Please use credit card.',
        errorType: 'processing_error',
      };
    } catch (error: any) {
      console.error('Google Pay error:', error);
      return {
        success: false,
        error: error.message || 'Google Pay processing failed',
        errorType: 'processing_error',
      };
    }
  }

  /**
   * Validate card details
   */
  private validateCardDetails(card: CardDetails): { valid: boolean; error?: string } {
    // Remove spaces and dashes from card number
    const cardNumber = card.number.replace(/[\s-]/g, '');

    // Validate card number (Luhn algorithm)
    if (!this.luhnCheck(cardNumber)) {
      return { valid: false, error: 'Invalid card number' };
    }

    // Validate expiration
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1;
    
    if (card.expYear < currentYear || 
        (card.expYear === currentYear && card.expMonth < currentMonth)) {
      return { valid: false, error: 'Card has expired' };
    }

    // Validate CVC
    if (!/^\d{3,4}$/.test(card.cvc)) {
      return { valid: false, error: 'Invalid CVC' };
    }

    return { valid: true };
  }

  /**
   * Luhn algorithm for card validation
   */
  private luhnCheck(cardNumber: string): boolean {
    let sum = 0;
    let isEven = false;

    for (let i = cardNumber.length - 1; i >= 0; i--) {
      let digit = parseInt(cardNumber[i], 10);

      if (isEven) {
        digit *= 2;
        if (digit > 9) {
          digit -= 9;
        }
      }

      sum += digit;
      isEven = !isEven;
    }

    return sum % 10 === 0;
  }

  /**
   * Create Payment Intent (backend call)
   */
  private async createPaymentIntent(request: PaymentRequest): Promise<{ clientSecret: string } | null> {
    try {
      const response = await fetch(`${STRIPE_CONFIG.apiEndpoint}/payment/create-intent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: request.amount,
          currency: request.currency,
          businessId: request.businessId,
          customerId: request.customerId,
          customerName: request.customerName,
          serviceName: request.serviceName,
          metadata: {
            serviceName: request.serviceName,
            customerId: request.customerId,
            businessId: request.businessId,
            ...request.metadata,
          },
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to create payment intent');
      }

      const data = await response.json();
      return { clientSecret: data.clientSecret };
    } catch (error) {
      console.error('Error creating payment intent:', error);
      return null;
    }
  }

  /**
   * Confirm Card Payment
   */
  private async confirmCardPayment(
    clientSecret: string,
    card: CardDetails
  ): Promise<PaymentResult> {
    try {
      // Web requires Stripe Elements/Payment Element integration.
      if (Platform.OS === 'web') {
        return {
          success: false,
          error: 'Web card confirmation requires Stripe Elements and is not configured yet.',
          errorType: 'processing_error',
        };
      }

      const stripeNative = require('@stripe/stripe-react-native');
      const initPaymentSheet = stripeNative?.initPaymentSheet;
      const presentPaymentSheet = stripeNative?.presentPaymentSheet;

      if (!initPaymentSheet || !presentPaymentSheet) {
        return {
          success: false,
          error: 'Stripe SDK is not available on this device.',
          errorType: 'processing_error',
        };
      }

      const initResult = await initPaymentSheet({
        merchantDisplayName: STRIPE_CONFIG.merchantName,
        merchantCountryCode: 'US',
        paymentIntentClientSecret: clientSecret,
        applePay: {
          merchantCountryCode: 'US',
        },
        googlePay: {
          merchantCountryCode: 'US',
          testEnv: __DEV__,
          currencyCode: 'USD',
        },
        defaultBillingDetails: {
          name: card?.name || 'Customer',
          address: {
            postalCode: card?.postalCode || '',
          },
        },
      });

      if (initResult?.error) {
        return {
          success: false,
          error: initResult.error.message || 'Unable to initialize payment sheet',
          errorType: 'processing_error',
        };
      }

      const presentResult = await presentPaymentSheet();
      if (presentResult?.error) {
        return {
          success: false,
          error: presentResult.error.message || 'Payment failed or was cancelled',
          errorType: 'card_error',
        };
      }

      return {
        success: true,
        transactionId: `pi_${Date.now()}`,
        paymentMethod: 'credit_card',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Payment confirmation failed',
        errorType: 'processing_error',
      };
    }
  }

  /**
   * Get card brand from number
   */
  getCardBrand(cardNumber: string): string {
    const cleanNumber = cardNumber.replace(/[\s-]/g, '');
    
    if (/^4/.test(cleanNumber)) return 'Visa';
    if (/^5[1-5]/.test(cleanNumber)) return 'Mastercard';
    if (/^3[47]/.test(cleanNumber)) return 'American Express';
    if (/^6(?:011|5)/.test(cleanNumber)) return 'Discover';
    
    return 'Unknown';
  }

  /**
   * Format card number for display
   */
  formatCardNumber(cardNumber: string): string {
    const cleaned = cardNumber.replace(/[\s-]/g, '');
    const groups = cleaned.match(/.{1,4}/g) || [];
    return groups.join(' ');
  }

  /**
   * Mask card number for display (e.g., **** **** **** 1234)
   */
  maskCardNumber(cardNumber: string): string {
    const cleaned = cardNumber.replace(/[\s-]/g, '');
    if (cleaned.length < 4) return cleaned;
    
    const lastFour = cleaned.slice(-4);
    return `**** **** **** ${lastFour}`;
  }

  /**
   * Get payment fee breakdown
   */
  getPaymentFeeBreakdown(amount: number): {
    subtotal: number;
    stripeFee: number;
    platformFee: number;
    total: number;
    businessReceives: number;
  } {
    const stripeFeePercent = 0.029;  // 2.9%
    const stripeFeeFixed = 0.30;     // $0.30
    const platformFeePercent = 0.05;  // 5% BookAPT fee
    
    const stripeFee = (amount * stripeFeePercent) + stripeFeeFixed;
    const platformFee = amount * platformFeePercent;
    const businessReceives = amount - stripeFee - platformFee;
    
    return {
      subtotal: amount,
      stripeFee: Number(stripeFee.toFixed(2)),
      platformFee: Number(platformFee.toFixed(2)),
      total: amount,
      businessReceives: Number(businessReceives.toFixed(2)),
    };
  }
}

export interface StripeRefundResult {
  success: boolean;
  refundId?: string;
  status?: string;
  error?: string;
}

/**
 * Partial (or full, if amountUsd omitted) refund of a real Stripe
 * PaymentIntent — used when a merchant edits an order down after the
 * customer already paid via the web Stripe checkout path.
 */
export async function refundStripePayment(
  paymentIntentId: string,
  amountUsd?: number
): Promise<StripeRefundResult> {
  try {
    const response = await fetch(`${STRIPE_CONFIG.apiEndpoint}/payment/refund`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ paymentIntentId, ...(amountUsd != null ? { amountUsd } : {}) }),
    });
    return await response.json();
  } catch (error: any) {
    return { success: false, error: error?.message || 'Could not refund payment.' };
  }
}

// Export singleton instance
export const stripePaymentService = new StripePaymentService();
export default stripePaymentService;





